import { Component, DestroyRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, map, Observable } from 'rxjs';
import { ClientCompanyViewModel, ClientSystemSettingValueModel, ClientSystemSettingViewModel, ListItem } from '../../core/api/models';
import { ClientsApiService, SystemSettingsApiService } from '../../core/api/services';
import { Utils } from '../../core/utils';
import { AlertService } from '../../shared/services/alert.service';
import { Editor, editorConfig } from '../../shared/ckeditor/ckeditor-config';

interface SettingEditor {
  item: ClientSystemSettingValueModel;
  setting: ClientSystemSettingViewModel;
  kind: string;
  control: FormControl;
  options: ListItem[];
  error?: string;
  saving: boolean;
  inputID: string;
  scopeName: string;
}

@Component({
  selector: 'app-client-system-settings',
  templateUrl: './client-system-settings.component.html',
  standalone: false
})
export class ClientSystemSettingsComponent implements OnChanges {
  private readonly destroyRef = inject(DestroyRef);
  private request = 0;
  @Input({ required: true }) clientID = 0;
  @Input() companies: ClientCompanyViewModel[] = [];
  @Input() systemSettingID?: number;
  @Output() savingChange = new EventEmitter<boolean>();
  companyID: number | null = null;
  editors: SettingEditor[] = [];
  featureNames = new Map<number, string>();
  featureID: number | null = null;
  search = '';
  loading = false;
  saving = false;
  readonly Editor = Editor;
  readonly editorConfig = editorConfig;
  readonly referenceSources: Record<string, string> = {
    photo: 'Photo', user: 'User', form: 'Form', userloginpolicy: 'UserLoginPolicy',
    payperiodfrequency: 'PayPeriodFrequency', servicesetting: 'ServiceSetting',
    staffingcancellationreason: 'StaffingCancellationReason'
  };

  constructor(
    private readonly clientsApi: ClientsApiService,
    private readonly definitionsApi: SystemSettingsApiService,
    private readonly alertService: AlertService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['clientID'] && !changes['systemSettingID']) return;
    ++this.request;
    this.saving = false;
    this.savingChange.emit(false);
    this.editors = [];
    this.companyID = null;
    this.load();
  }

  get visibleEditors(): SettingEditor[] {
    const search = this.search.trim().toLowerCase();
    return this.editors.filter(editor =>
      (!this.featureID || editor.setting.SystemFeature === this.featureID) &&
      (!search || (editor.setting.SettingName + ' ' + (editor.setting.Description ?? '')).toLowerCase().includes(search)));
  }

  load(): void {
    if (!this.clientID || this.saving) return;
    const request = ++this.request;
    const clientID = this.clientID;
    this.loading = true;
    this.editors = [];
    const settings: Observable<{ values: ClientSystemSettingViewModel[]; companies: ListItem[] }> = 
       this.clientsApi.GetClientSystemSettings({ clientID, body: { systemSettingID: this.systemSettingID  } }).pipe(
        map(values => ({ values, companies: this.companies.map(company => ({ Value: company.ID, Text: company.BusinessName })) })));
    forkJoin({
      settings,
      types: this.definitionsApi.GetSystemSettingTypes(),
      features: this.definitionsApi.GetSystemFeatures()
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: data => {
        if (request !== this.request) return;
        this.featureNames = new Map(data.features.map(feature => [feature.ID ?? 0, feature.Title || feature.Name || '']));
        const types = new Map(data.types.map(type => [type.ID, (type.SystemSettingType ?? '').toLowerCase().replace(/[^a-z]/g, '')]));
        const companyNames = new Map(data.settings.companies.map(company => [company.Value, company.Text]));
        this.editors = data.settings.values.flatMap(item => {
          const type = types.get(item.SystemSettingType) ?? '';
          const kind = type === 'curreny' ? 'currency' : type;
          const validators = item.Required ? [Validators.required] : [];
          // False is a valid, explicitly disabled checkbox setting.
          if (kind === 'checkbox') validators.length = 0;
          if (kind === 'bytes') validators.length = 0;
          if (['text', 'securetext', 'password'].includes(kind)) validators.push(Validators.maxLength(4000));
          if (!item.Values)
            return [];

          return item.Values.map(itemValue => { 

          const editor: SettingEditor = {
            item: itemValue, setting: item, kind, options: [], saving: false,
            inputID: 'setting-' + item.SystemSetting + '-' + (itemValue.CompanyID ?? 'enterprise'),
            scopeName: itemValue.CompanyID == null ? 'Enterprise' : companyNames.get(itemValue.CompanyID) || 'Company #' + itemValue.CompanyID,
            control: new FormControl(null, validators)
          };
          if (kind === 'password' && item.Required) {
            editor.control.removeValidators(Validators.required);
            editor.control.addValidators(control =>
              itemValue.BitValue === true && !control.dirty ? null : Validators.required(control));
          }
          try {
            const options = (item.Settings as unknown as {
              Options?: ListItem[]; options?: ListItem[]; Min?: number; Max?: number;
            }) ?? {};
            editor.options = options.Options ?? options.options ?? [];
            if (!Array.isArray(editor.options)) throw new Error('Invalid options');
            if (options.Min != null) editor.control.addValidators(Validators.min(options.Min));
            if (options.Max != null) editor.control.addValidators(Validators.max(options.Max));
          } catch {
            editor.error = 'The setting options or saved selection are invalid JSON. Correct the definition before editing.';
          }
          if (!['text', 'securetext', 'password', 'bigtext', 'bigtexteditor', 'date', 'number', 'decimal', 'currency', 'checkbox',
            'oneoption', 'multipleoptions', 'file', 'image', 'bytes', ...Object.keys(this.referenceSources)].includes(kind)) {
            editor.error = 'This setting has an unsupported storage type.';
          }
          this.setEditorValue(editor, item);
          return editor;
          
          
        });
          
        });
        const sources = [...new Set(this.editors.map(editor => editor.setting!.SystemSettingType!).filter(Boolean))];
        const lookups: Record<string, Observable<ListItem[]>> = {};
        for (const source of sources) {
          lookups[source] = this.clientsApi.GetClientFormOptions({ clientID: this.clientID, body: { systemSettingTypeID: source } });
        }
        if (!sources.length) {
          this.loading = false;
          return;
        }
        // Retrieve each source once, even when many settings share its options.
        forkJoin(lookups).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: result => {
            if (request !== this.request) return;
            for (const editor of this.editors) {
              const source = this.referenceSources[editor.kind];
              if (source) editor.options = result[source] ?? [];
            }
            this.loading = false;
          },
          error: error => {
            if (request !== this.request) return;
            for (const editor of this.editors) {
              if (this.referenceSources[editor.kind]) editor.error = 'Could not load the available choices. Reload settings to try again.';
            }
            this.loading = false;
            this.alertService.error(Utils.getAjaxError(error));
          }
        });
      },
      error: error => {
        if (request !== this.request) return;
        this.loading = false;
        this.alertService.error(Utils.getAjaxError(error));
      }
    });
  }

  setEditorValue(editor: SettingEditor, item: ClientSystemSettingValueModel): void {
    editor.item = item;
    let value: unknown = item.Value ?? null;
    if (editor.kind === 'password') value = null;
    else if (editor.kind === 'checkbox') value = item.BitValue ?? null;
    else if (editor.kind === 'number' || editor.kind === 'oneoption' || this.referenceSources[editor.kind]) value = item.IntValue ?? null;
    else if (editor.kind === 'decimal' || editor.kind === 'currency') value = item.DecimalValue ?? null;
    else if (editor.kind === 'date') {
      value = item.DateValue ? item.DateValue.replace(/(?:Z|[+-]\d{2}:\d{2})$/, '').slice(0, 23) : null;
    } else if (editor.kind === 'multipleoptions') {
      try {
        const selected: unknown = item.Value ? JSON.parse(item.Value) : [];
        if (!Array.isArray(selected)) throw new Error('Invalid selection');
        value = editor.options.length ? selected : JSON.stringify(selected);
      } catch {
        editor.error = 'The saved selection is not a JSON array. Correct it before editing.';
      }
    }
    editor.control.reset(value);
  }

  save(editor: SettingEditor, useDefault = false): void {
    const companyID = editor.item.CompanyID ?? null;
    if (this.saving || this.loading || editor.error || (companyID !== null && !editor.setting.ShowOnCompanySettings)) return;
    editor.control.markAsTouched();
    if (!useDefault && editor.control.invalid) {
      this.alertService.error('Enter a valid value for ' + editor.setting.SettingName + '.');
      return;
    }
    const clientID = this.clientID;
    const request = this.request;
    let value = editor.control.value;
    if (!useDefault && editor.kind === 'multipleoptions') {
      try {
        if (!editor.options.length) value = JSON.parse(value || '[]');
        if (!Array.isArray(value) || (editor.setting.Required && !value.length)) throw new Error('Invalid selection');
      } catch {
        this.alertService.error('Enter a valid JSON array of selected values for ' + editor.setting.SettingName + '.');
        return;
      }
    }
    if (!useDefault && (editor.kind === 'number' || editor.kind === 'oneoption' || this.referenceSources[editor.kind]) &&
        value != null && (!Number.isInteger(value) || value < -2147483648 || value > 2147483647)) {
      this.alertService.error('Enter a valid whole number for ' + editor.setting.SettingName + '.');
      return;
    }
    const body: ClientSystemSettingValueModel = {
      SystemSetting: editor.setting.SystemSetting,
      CompanyID: companyID ?? undefined
    };
    if (editor.kind === 'password' && !useDefault && !editor.control.dirty && editor.item.BitValue === true)
      body.BitValue = true;
    if (!useDefault) {
      if (editor.kind === 'checkbox') body.BitValue = value ?? undefined;
      else if (editor.kind === 'number' || editor.kind === 'oneoption' || this.referenceSources[editor.kind]) body.IntValue = value ?? undefined;
      else if (editor.kind === 'decimal' || editor.kind === 'currency') body.DecimalValue = value ?? undefined;
      // Settings use SQL datetime2 wall-clock values, not instants with a timezone.
      else if (editor.kind === 'date') body.DateValue = value ? String(value) : undefined;
      else body.Value = editor.kind === 'multipleoptions' ? JSON.stringify(value) : value ?? undefined;
    }
    editor.saving = this.saving = true;
    this.savingChange.emit(true);
    this.clientsApi.SaveClientSystemSetting({ clientID, body }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: saved => {
        if (request !== this.request) return;
        editor.saving = this.saving = false;
        this.savingChange.emit(false);
        //this.setEditorValue(editor, saved);
        if (this.systemSettingID && companyID === null) {
          for (const companyEditor of this.editors) {
            if (companyEditor.item.CompanyID == null || companyEditor.control.dirty) continue;
            //this.setEditorValue(companyEditor, { ...saved, CompanyID: companyEditor.item.CompanyID, HasOwnValue: false });
          }
        }
        this.alertService.success(editor.setting.SettingName + (useDefault ? ' now uses the inherited value.' : ' saved successfully.'));
      },
      error: error => {
        if (request !== this.request) return;
        editor.saving = this.saving = false;
        this.savingChange.emit(false);
        this.alertService.error(Utils.getAjaxError(error));
      }
    });
  }

  upload(editor: SettingEditor, input: HTMLInputElement): void {
    const file = input.files?.[0];
    const companyID = editor.item.CompanyID ?? null;
    if (!file || this.saving || this.loading || !editor.item.SystemSetting || (companyID !== null && !editor.setting.ShowOnCompanySettings)) return;
    if (file.size === 0 || file.size > 50 * 1024 * 1024) {
      input.value = '';
      this.alertService.error('Select a non-empty file of at most 50 MB.');
      return;
    }
    const clientID = this.clientID;
    const request = this.request;
    editor.saving = this.saving = true;
    this.savingChange.emit(true);
    this.clientsApi.UploadClientSystemSetting({
      clientID,
      body: { systemSettingID: editor.item.SystemSetting, companyID: companyID ?? undefined, file }
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        if (request !== this.request) return;
        editor.saving = this.saving = false;
        this.savingChange.emit(false);
        input.value = '';
        this.alertService.success(editor.setting.SettingName + ' uploaded successfully.');
        this.load();
      },
      error: error => {
        if (request !== this.request) return;
        editor.saving = this.saving = false;
        this.savingChange.emit(false);
        input.value = '';
        this.alertService.error(Utils.getAjaxError(error));
      }
    });
  }
}
